import { useEffect, memo, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import type { DbStartAtIndex } from "@/db/schema"
import { db } from "@/db/schema"
import { deleteTask } from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { DetailHeader } from "./components/DetailHeader"
import { DetailNotes } from "./components/DetailNotes"
import { DetailChecklist } from "./components/DetailChecklist"
import { DetailDates } from "./components/DetailDates"
import { DetailTags } from "./components/DetailTags"
import { DetailQuickActions } from "./components/DetailQuickActions"
import { PlanBadge } from "@/features/plans/PlanBadge"
import { RecurrenceHistory, RecurrenceSheet, useRecurrence } from "@/features/recurrence"
import { TimeTracker } from "@/features/time-tracking/TimeTracker"
import { hasMeaningfulData, isNewlyCreated } from "./utils/hasMeaningfulData"
import { useTaskDetailShortcuts } from "./hooks/useTaskDetailShortcuts"
import { useTaskDetailAutoClose } from "./hooks/useTaskDetailAutoClose"
import { parseDbDate } from "@totodo/operations"

interface TaskDetailProps {
  task: DbObject
  startAtData?: DbStartAtIndex
  /** Whether to auto-focus the title for editing */
  editTitle?: boolean
  /** Hide the bottom action bar (dates + quick actions) */
  hideActions?: boolean
  /** Show container picker in footer (for quick-add, AI preview, etc.) */
  showContainer?: boolean
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  onClose: () => void
}

/**
 * Inline expanded task detail card.
 *
 * v1 pattern: `TaskCard` toggles between `CardPreview` (collapsed) and
 * `CardFull` (expanded) based on URL query `?tid=<taskId>`.
 *
 * Animation + scroll are handled by the parent TaskItem wrapper
 * (using `.task-card-open` class and `enhancedSmoothScroll`).
 * This component just renders the detail content.
 */
export const TaskDetail = memo(function TaskDetail({
  task,
  startAtData,
  editTitle,
  hideActions,
  showContainer = false,
  onComplete,
  onUncomplete,
  onClose,
}: TaskDetailProps) {
  const { t } = useTranslation()
  // Tag input visibility — controlled by quick-action icon
  const [showTagInput, setShowTagInput] = useState(false)
  const [showRecurrenceSheet, setShowRecurrenceSheet] = useState(false)
  const handleCloseTagInput = useCallback(() => setShowTagInput(false), [])
  const handleRequestTagInput = useCallback(() => setShowTagInput(true), [])
  const hasTags = !!(task.tags && task.tags.length > 0)

  // --- Reminder live query (own query so parent doesn't need to supply it)
  const [reminderData] = useLiveQueryImmediate(
    async () => {
      const taskId = task.id
      if (!taskId) return null
      const now = dayjs().valueOf().toString()
      const reminders = await db.reminder
        .where("taskId")
        .equals(taskId)
        .filter(
          (r) =>
            (r.isDeleted === 0 || r.isDeleted === undefined) &&
            !!r.fireAt &&
            r.fireAt > now,
        )
        .toArray()
      if (!reminders.length) return null
      // Sort by fireAt ascending
      reminders.sort((a, b) => (parseDbDate(a.fireAt) ?? 0) - (parseDbDate(b.fireAt) ?? 0))
      return { all: reminders, first: reminders[0], count: reminders.length }
    },
    ["task-detail-reminder", task.id],
    null,
  )
  const existingReminder = reminderData?.first
  const allReminders = reminderData?.all ?? []
  const reminderCount = reminderData?.count ?? 0

  // Recurrence data for template or instance tasks
  const recurrenceTemplateId = task.isRecurringTemplate ? task.id : task.recurringTemplateId
  const recurrence = useRecurrence(recurrenceTemplateId ?? undefined)

  // §8.5 Task-detail keyboard shortcuts (P1 context)
  useTaskDetailShortcuts(task, onClose)

  // §8.11 Auto-close when task is deleted/hidden externally
  useTaskDetailAutoClose(task, onClose)

  // §31 Escape-guard: delete newly created empty tasks, otherwise just close.
  // ChecklistContainer's Escape handler uses stopImmediatePropagation
  // when clearing selection, so we never see that event here.
  const handleEscapeGuard = useCallback(async () => {
    const title = task.title?.trim() ?? ""
    if (title === "" && isNewlyCreated(task) && task.id) {
      const hasMeaningful = await hasMeaningfulData(task)
      if (!hasMeaningful) {
        deleteTask(task.id)
      }
    }
    onClose()
  }, [task, onClose])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return

      // If an editable field has focus, let it handle Escape (blur/revert)
      const active = document.activeElement
      if (
        active instanceof HTMLElement &&
        (active.isContentEditable ||
          active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA")
      ) {
        return
      }

      e.preventDefault()
      handleEscapeGuard()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [handleEscapeGuard])

  return (
    <div
      className={cn("rounded-lg", "bg-card")}
      role="region"
      aria-label={t("Task detail: {{title}}", { title: task.title })}
    >
      {/* Header — checkbox + editable title */}
      <DetailHeader
        task={task}
        startAtData={startAtData}
        editOnMount={editTitle}
        onComplete={onComplete}
        onUncomplete={onUncomplete}
        onClose={onClose}
        onDelete={onClose}
      />

      {/* Plan badge — shown when task belongs to a Smart Plan */}
      {(task.planId || task.anchorTaskId) && (
        <div className="ml-4 desk:ml-12 mr-4 -mt-1 mb-1">
          <PlanBadge task={task} />
        </div>
      )}

      {/* Recurrence badge + history — shown on template and instance tasks */}
      {recurrence && (
        <div className="ml-4 desk:ml-12 mr-4 -mt-1 mb-1">
          <RecurrenceHistory
            templateId={recurrence.id}
            recurrence={recurrence}
            onEditRecurrence={() => setShowRecurrenceSheet(true)}
            isInstance={!task.isRecurringTemplate}
          />
          <RecurrenceSheet
            open={showRecurrenceSheet}
            onOpenChange={setShowRecurrenceSheet}
            task={task}
            startAtData={startAtData}
          />
        </div>
      )}

      {/* Progressive target — shown on recurring instances */}
      {!recurrence && task.progressiveTarget != null && (
        <div className="ml-4 desk:ml-12 mr-4 -mt-1 mb-1">
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            🎯 {task.progressiveTarget}
          </span>
        </div>
      )}

      {/* §8.4 — Sections use focus-within tint for keyboard navigation visibility */}
      <div className="ml-4 desk:ml-12 mr-4 space-y-0">
        {/* Notes */}
        <div className="rounded">
          <DetailNotes task={task} />
        </div>

        {/* Checklist — under notes, multi-user optimized */}
        <DetailChecklist task={task} />
      </div>

      {/* Tags — hidden when empty, visible when tags exist or quick-action triggered */}
      {(hasTags || showTagInput) && (
        <div className="detail-section-enter relative z-10 rounded">
          <DetailTags
            task={task}
            autoFocus={showTagInput}
            onClose={handleCloseTagInput}
          />
        </div>
      )}

      {/* Time tracking — below content (Jira/ClickUp pattern: content-first, reference data last) */}
      {(task.timeSpentMs != null || task.timerStartedAt) && (
        <div className="ml-4 desk:ml-12 mr-4 mt-2 border-t border-border/40 pt-2">
          <TimeTracker task={task} />
        </div>
      )}

      {/* Date bar — dates left, quick-action icons right (v1 pattern) */}
      {!hideActions && (
        <DetailDates
          task={task}
          startAtData={startAtData}
          existingReminder={existingReminder}
          allReminders={allReminders}
          reminderCount={reminderCount}
          showContainer={showContainer}
          quickActions={
            <DetailQuickActions
              task={task}
              startAtData={startAtData}
              existingReminder={existingReminder}
              allReminders={allReminders}
              onRequestTagInput={handleRequestTagInput}
            />
          }
        />
      )}
    </div>
  )
})
